/**
 * THE VIREO SEAM.
 *
 * Every `ax-*` class in the CRM lives in this file. Screens import these
 * primitives and never write template classes directly, so when the real Vireo
 * vocabulary is known, this file changes and no screen does.
 *
 * The class names below are placeholders in Vireo's `ax-` idiom. Swap them for
 * the real ones; keep the prop shapes.
 */

import type { ReactNode } from 'react'

type Variant = 'solid' | 'subtle' | 'ghost' | 'danger'

const BUTTON: Record<Variant, string> = {
  solid: 'ax-btn ax-btn-primary',
  subtle: 'ax-btn ax-btn-secondary',
  ghost: 'ax-btn ax-btn-ghost',
  danger: 'ax-btn ax-btn-danger',
}

export function Button({
  variant = 'subtle',
  type = 'button',
  disabled,
  onClick,
  children,
}: {
  variant?: Variant
  type?: 'button' | 'submit'
  disabled?: boolean | undefined
  onClick?: () => void
  children: ReactNode
}) {
  return (
    <button className={BUTTON[variant]} type={type} disabled={disabled} onClick={onClick}>
      {children}
    </button>
  )
}

/** Status pill. `color` matches the lookup tables' colour vocabulary. */
export function Badge({ color, children }: { color: string; children: ReactNode }) {
  return <span className={`ax-badge ax-badge-${color}`}>{children}</span>
}

export function PageHeader({
  title,
  breadcrumbs,
  actions,
}: {
  title: string
  breadcrumbs?: ReactNode
  actions?: ReactNode
}) {
  return (
    <header className="ax-page-header">
      <div className="ax-page-header-left">
        {breadcrumbs ?? <h1 className="ax-page-title">{title}</h1>}
      </div>
      <div className="ax-page-header-right">{actions}</div>
    </header>
  )
}

export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="ax-toolbar">{children}</div>
}

export function SearchInput({
  name,
  defaultValue,
  placeholder,
}: {
  name: string
  defaultValue?: string | undefined
  placeholder?: string | undefined
}) {
  return (
    <input
      className="ax-input ax-input-search"
      type="search"
      name={name}
      defaultValue={defaultValue ?? ''}
      placeholder={placeholder ?? 'Search'}
    />
  )
}

/**
 * Controlled only when `onChange` is supplied.
 *
 * Passing `value` unconditionally makes a `<select>` controlled, so without a
 * handler React re-renders it back to the same value on every change and the
 * user cannot pick anything. Forms want the opposite: set the starting value,
 * then let the browser own it and read it from FormData on submit. `value` is
 * therefore the initial value when uncontrolled, and the live one when not.
 */
export function Select({
  name,
  value,
  onChange,
  options,
  disabled,
}: {
  name: string
  value: string
  onChange?: (value: string) => void
  options: readonly { value: string; label: string }[]
  disabled?: boolean | undefined
}) {
  const children = options.map((option) => (
    <option key={option.value} value={option.value}>
      {option.label}
    </option>
  ))

  if (onChange === undefined) {
    return (
      <select className="ax-select" name={name} defaultValue={value} disabled={disabled}>
        {children}
      </select>
    )
  }

  return (
    <select
      className="ax-select"
      name={name}
      value={value}
      disabled={disabled}
      onChange={(event) => onChange(event.currentTarget.value)}
    >
      {children}
    </select>
  )
}

export function Field({
  label,
  error,
  children,
}: {
  label: string
  error?: string[] | undefined
  children: ReactNode
}) {
  return (
    <label className="ax-field">
      <span className="ax-field-label">{label}</span>
      {children}
      {error && error.length > 0 ? <span className="ax-field-error">{error[0]}</span> : null}
    </label>
  )
}

export function TextInput({
  name,
  defaultValue,
  placeholder,
  type = 'text',
  required,
}: {
  name: string
  defaultValue?: string | null | undefined
  placeholder?: string | undefined
  type?: 'text' | 'email' | 'tel' | 'url' | 'number' | 'date' | 'datetime-local' | undefined
  required?: boolean | undefined
}) {
  return (
    <input
      className="ax-input"
      type={type}
      name={name}
      defaultValue={defaultValue ?? ''}
      placeholder={placeholder ?? ''}
      required={required ?? false}
    />
  )
}

export function Textarea({
  name,
  defaultValue,
  placeholder,
  rows = 3,
}: {
  name: string
  defaultValue?: string | null | undefined
  placeholder?: string | undefined
  rows?: number | undefined
}) {
  return (
    <textarea
      className="ax-textarea"
      name={name}
      rows={rows}
      defaultValue={defaultValue ?? ''}
      placeholder={placeholder ?? ''}
    />
  )
}

export function Tabs<T extends string>({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly { id: T; label: string; count?: number }[]
  active: T
  onSelect: (id: T) => void
}) {
  return (
    <nav className="ax-tabs" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          type="button"
          aria-selected={active === tab.id}
          className={active === tab.id ? 'ax-tab ax-tab-active' : 'ax-tab'}
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined ? <span className="ax-tab-count">{tab.count}</span> : null}
        </button>
      ))}
    </nav>
  )
}

export function Table({ children }: { children: ReactNode }) {
  // The wrapper owns horizontal overflow so the page body never scrolls sideways.
  return (
    <div className="ax-table-wrap">
      <table className="ax-table">{children}</table>
    </div>
  )
}

export function EmptyState({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <div className="ax-empty">
      <p className="ax-empty-title">{title}</p>
      {action}
    </div>
  )
}

export function Spinner() {
  return <span className="ax-spinner" role="status" aria-label="Loading" />
}

export function Card({ children, onClick }: { children: ReactNode; onClick?: () => void }) {
  return (
    <div className="ax-card" onClick={onClick} role={onClick ? 'button' : undefined}>
      {children}
    </div>
  )
}
